import React from "react";
import { useQuery } from "@tanstack/react-query";
import IModelData from "../../types/ModelData";
import { FMEPageHeader, useModelContext, ScenarioSensitivitySelector } from '../../components/Layout';
import FinancialStatementsTable from '../../components/FinancialStatementsTable';
import { getScenarioOutputRoot, getOutputModelDataChildren } from '../../shared/api/output';
import { getSensitivity } from '../../shared/api/scenarios';

// Types de financial statements que nous voulons afficher
const FINANCIAL_STATEMENT_TYPES = [
    { type: 'p&l', title: 'Profit & Loss' },
    { type: 'balance_sheet', title: 'Balance Sheet' },
    { type: 'cash_flow_statement', title: 'Cash Flow Statement' },
    { type: 'cash_flow_waterfall', title: 'Cash Flow Waterfall' },
    { type: 'uses_sources', title: 'Uses & Sources' }
];

interface CompanyStatements {
    companyName: string;
    companyId: number;
    statements: {
        [key: string]: IModelData;
    };
}

export default function FinancialStatements() {
    const { model, currentScenario, currentSensitivity, currentEntity, computeGeneration } = useModelContext();

    if (!currentEntity || !currentScenario || !model) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm text-slate-500 font-medium">Loading...</span>
                </div>
            </div>
        );
    }

    return <FinancialStatementsContent currentEntity={currentEntity} model={model} currentScenario={currentScenario} currentSensitivity={currentSensitivity} computeGeneration={computeGeneration} />;
}

// Content component: business logic and rendering
function FinancialStatementsContent({ currentEntity, model, currentScenario, currentSensitivity, computeGeneration }) {
    const scenarioId = currentEntity.type === 'scenario' ? currentEntity.id : currentEntity.scenario_id;
    const sensitivityId = currentEntity.type === 'sensitivity' ? currentEntity.id : 0;

    const { data: companiesStatements = [], isLoading: loading } = useQuery({
        queryKey: ["financialStatements", scenarioId, sensitivityId, computeGeneration],
        queryFn: async () => {
            const responseData = await getScenarioOutputRoot(scenarioId);
            const outputData: IModelData[] = responseData.output || [];

            // Pre-fetch sensitivity data once (reused for all items)
            let sensOutputMap: Map<number, any> | null = null;
            if (sensitivityId > 0) {
                try {
                    const sensData = await getSensitivity(sensitivityId);
                    if (sensData?.output) {
                        sensOutputMap = new Map<number, any>();
                        sensData.output.forEach((item: any) => sensOutputMap!.set(item.id, item));
                    }
                } catch (err) {
                    console.error('Error loading sensitivity output:', err);
                }
            }

            // Helper: merge sensitivity fields into an item
            const mergeSensitivity = (item: IModelData): IModelData => {
                if (!sensOutputMap) return item;
                const sensItem = sensOutputMap.get(item.id);
                if (sensItem && sensItem.fields) {
                    return { ...item, fields: { ...item.fields, ...sensItem.fields } };
                }
                return item;
            };

            // Find company items from level-0 output
            const companies = outputData.filter(md => md.type === 'company');

            // For each company, fetch its direct children (statements)
            const companiesMap = new Map<number, CompanyStatements>();

            await Promise.all(companies.map(async (company) => {
                const companyStatements: CompanyStatements = {
                    companyName: company.name,
                    companyId: company.id,
                    statements: {}
                };

                try {
                    const children: IModelData[] = await getOutputModelDataChildren(company.id);

                    FINANCIAL_STATEMENT_TYPES.forEach(({ type }) => {
                        const statement = children.find(md => md.type === type);
                        if (statement) {
                            companyStatements.statements[type] = mergeSensitivity(statement);
                        }
                    });
                } catch (err) {
                    console.error(`Error loading children for company ${company.name}:`, err);
                }

                companiesMap.set(company.id, companyStatements);
            }));

            return Array.from(companiesMap.values());
        },
        enabled: scenarioId > 0,
        staleTime: 0,
    });

    return (
        <div className="flex flex-col h-full">
            <FMEPageHeader
                title="Financial Statements"
                breadcrumb={[model.name]}
                description="Review income statements, balance sheets, and cash flow data."
                actions={<ScenarioSensitivitySelector />}
            />

            <div className="px-8 py-6 flex-1 min-h-0 overflow-y-auto">
                {loading ? (
                    <div className="flex items-center justify-center py-16">
                        <div className="flex flex-col items-center gap-3">
                            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                            <p className="text-slate-500">Loading financial statements...</p>
                        </div>
                    </div>
                ) : companiesStatements.length > 0 ? (
                    <FinancialStatementsTable data={companiesStatements} />
                ) : (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                        <div className="size-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                            <span className="material-symbols-outlined text-[32px] text-slate-400">description</span>
                        </div>
                        <h3 className="text-lg font-medium text-slate-900 mb-2">No Financial Statements</h3>
                        <p className="text-slate-500 max-w-md">
                            Please design your architecture and complete all required inputs to see the computed results.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
